import {
  Heart,
  CheckCircle,
  HandTap,
  PersonArmsSpread,
  Eye,
  ChatText,
  ShieldCheck,
  Keyboard,
} from "@phosphor-icons/react";
import { SectionShell } from "../Primitives";

const GUIDELINES = [
  { icon: Eye, t: "Contrast that clears AA", d: "Body text ≥ 4.5:1, large text & UI ≥ 3:1 — verified in light and dark." },
  { icon: HandTap, t: "Generous touch targets", d: "Every tappable element is at least 48 × 48px, with comfortable spacing between." },
  { icon: PersonArmsSpread, t: "Reduced motion", d: "All animation respects prefers-reduced-motion with calm static states." },
  { icon: ChatText, t: "Plain, kind language", d: "Short sentences, friendly tone. Mistakes invite a gentle 'try again', never blame." },
  { icon: Keyboard, t: "Keyboard & screen reader", d: "Full keyboard paths, semantic landmarks, labels and ARIA on every control." },
  { icon: CheckCircle, t: "Visible focus", d: "A clear 3px focus ring on every interactive element for keyboard users." },
  { icon: ShieldCheck, t: "Safe & calm", d: "No dark patterns, no flashing, no pressure. A protected space to learn." },
  { icon: Heart, t: "Readable type", d: "16px minimum, rounded humanist fonts and roomy line-height for early & dyslexic readers." },
];

export function AccessibilitySection() {
  return (
    <SectionShell
      id="accessibility"
      eyebrow="Accessibility"
      eyebrowIcon={Heart}
      title="Accessible & safe, by default"
      description="Accessibility is the highest priority — not a checklist at the end. These rules are baked into every token and component so inclusion comes for free."
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {GUIDELINES.map(({ icon: Icon, t, d }) => (
          <div key={t} className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/12 text-primary-strong">
              <Icon size={24} weight="fill" />
            </span>
            <p className="mt-4 font-display text-lg font-extrabold leading-tight">{t}</p>
            <p className="mt-2 text-sm font-medium leading-relaxed text-muted-foreground">{d}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-soft md:p-8">
          <p className="mb-5 text-sm font-bold uppercase tracking-wider text-muted-foreground">Touch targets</p>
          <div className="flex items-end gap-5">
            <div className="flex flex-col items-center gap-2">
              <button className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-glow">
                <CheckCircle size={22} weight="fill" />
              </button>
              <span className="text-xs font-bold text-teal-ink">48px · pass</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-glow">
                <CheckCircle size={28} weight="fill" />
              </button>
              <span className="text-xs font-bold text-teal-ink">64px · comfy</span>
            </div>
            <div className="flex flex-col items-center gap-2 opacity-70">
              <button className="flex h-8 w-8 items-center justify-center rounded-lg border-2 border-dashed border-destructive text-destructive-ink">
                <CheckCircle size={14} weight="bold" />
              </button>
              <span className="text-xs font-bold text-destructive-ink">32px · avoid</span>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-border bg-card p-6 shadow-soft md:p-8">
          <p className="mb-5 text-sm font-bold uppercase tracking-wider text-muted-foreground">Contrast pairs</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 rounded-2xl bg-primary px-4 py-3 font-bold text-primary-foreground">
              <CheckCircle size={18} weight="fill" /> White on orange
            </div>
            <div className="flex items-center gap-2 rounded-2xl bg-card px-4 py-3 font-bold text-primary-strong ring-1 ring-border">
              <CheckCircle size={18} weight="fill" /> Strong on white
            </div>
            <div className="flex items-center gap-2 rounded-2xl bg-foreground px-4 py-3 font-bold text-background">
              <CheckCircle size={18} weight="fill" /> Ink on canvas
            </div>
            <div className="flex items-center gap-2 rounded-2xl bg-teal/15 px-4 py-3 font-bold text-teal-ink">
              <CheckCircle size={18} weight="fill" /> Teal ink
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

export default AccessibilitySection;
